import get from 'lodash.get';

import { isUrlValid } from '../../../helpers/validationHelper';
import { paths } from '../create/constants/template';

/**
 * What a system admin saves on an event PlaceHub program under
 * `template.value.announcement`. Every field is optional: the template JSON is
 * free-form and programs created before the feature have no announcement.
 */
export interface ProgramAnnouncement {
  enabled?: boolean;
  title?: string;
  description?: string;
  callToAction?: string;
  link?: string;
}

/** The renderable banner once the raw fields have been checked. */
export interface ActiveProgramAnnouncement {
  title: string;
  description?: string;
  callToAction?: { label: string; href: string };
}

const trimmed = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const text = value.trim();
  return text ? text : undefined;
};

/** The raw announcement off a program record, or null when it has none. */
export const getProgramAnnouncement = (
  eventAmbassadorProgram: unknown
): ProgramAnnouncement | null => {
  const announcement: unknown = get(eventAmbassadorProgram, paths.ANNOUNCEMENT);
  return announcement && typeof announcement === 'object'
    ? (announcement as ProgramAnnouncement)
    : null;
};

/**
 * Turns the raw fields into a banner, ignoring the enabled flag so the edit
 * tab can preview a banner that is still switched off. Null without a title,
 * since a banner needs a heading. The call to action only comes through when
 * both its label and a valid http(s) link are present.
 */
export const toActiveAnnouncement = (
  announcement: ProgramAnnouncement
): ActiveProgramAnnouncement | null => {
  const title = trimmed(announcement.title);
  if (!title) {
    return null;
  }

  const label = trimmed(announcement.callToAction);
  const href = trimmed(announcement.link);

  return {
    title,
    description: trimmed(announcement.description),
    callToAction:
      label && href && isUrlValid(href) ? { label, href } : undefined
  };
};

/**
 * The banner charities should currently see for an event program, or null
 * when the announcement is off, missing or has no title.
 */
export const getActiveProgramAnnouncement = (
  eventAmbassadorProgram: unknown
): ActiveProgramAnnouncement | null => {
  const announcement = getProgramAnnouncement(eventAmbassadorProgram);
  if (!announcement || announcement.enabled !== true) {
    return null;
  }
  return toActiveAnnouncement(announcement);
};
